import clsx from 'clsx';
import {
  Activity,
  Bell,
  BookOpen,
  CalendarClock,
  ClipboardList,
  Cog,
  Factory,
  Gauge,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  Search,
  Tag,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { PERMISSIONS, type PermissionCode } from '@odr/shared';

import { useAuth } from '../lib/auth';
import { initials } from '../lib/format';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './NotificationBell';
import { SyncIndicator } from './SyncIndicator';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  permission?: PermissionCode;
  /** Shown in the phone bottom bar (max 5). */
  primary?: boolean;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Today', icon: LayoutDashboard, primary: true, end: true },
  { to: '/machines', label: 'Machines', icon: Factory, permission: PERMISSIONS.MACHINE_VIEW, primary: true },
  { to: '/scan', label: 'Scan', icon: QrCode, primary: true },
  { to: '/problems', label: 'Problems', icon: Tag, permission: PERMISSIONS.PROBLEM_VIEW, primary: true },
  { to: '/opl', label: 'OPL', icon: BookOpen, permission: PERMISSIONS.OPL_VIEW, primary: true },
  { to: '/checklists', label: 'Checklists', icon: ClipboardList, permission: PERMISSIONS.RUN_VIEW_OWN },
  { to: '/supervisor', label: 'Supervisor', icon: ClipboardList, permission: PERMISSIONS.DASHBOARD_SUPERVISOR },
  { to: '/maintenance', label: 'Maintenance', icon: Wrench, permission: PERMISSIONS.DASHBOARD_MAINTENANCE },
  { to: '/coach', label: 'AM Coach', icon: GraduationCap, permission: PERMISSIONS.DASHBOARD_COACH },
  { to: '/management', label: 'Management', icon: LayoutDashboard, permission: PERMISSIONS.DASHBOARD_MANAGEMENT },
  { to: '/training', label: 'Training', icon: GraduationCap },
  {
    to: '/engineering-standards',
    label: 'Standards',
    icon: Gauge,
    permission: PERMISSIONS.STANDARD_VIEW,
  },
  {
    to: '/analytics/cilt',
    label: 'CILT compliance',
    icon: CalendarClock,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    to: '/analytics/problems',
    label: 'Problem intelligence',
    icon: Activity,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    to: '/analytics/learning',
    label: 'Learning',
    icon: GraduationCap,
    permission: PERMISSIONS.TRAINING_VIEW_ALL,
  },
  { to: '/reports', label: 'Reports', icon: ClipboardList, permission: PERMISSIONS.REPORT_VIEW },
  // Shown to anyone with any administration area — the AM Coach needs the
  // checklist editor without holding user management.
  { to: '/admin', label: 'Admin', icon: Cog, permission: PERMISSIONS.TEMPLATE_VIEW },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const signOut = useAuth((s) => s.signOut);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Close the mobile drawer whenever navigation happens.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const visible = NAV.filter(
    (item) => !item.permission || user?.permissions.includes(item.permission),
  );
  const primary = visible.filter((item) => item.primary).slice(0, 5);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col bg-steel-100 lg:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-steel-800 bg-steel-900 lg:flex">
        <Link to="/" className="flex items-center gap-2 px-5 py-4 text-white">
          <span className="grid size-8 place-items-center rounded-lg bg-info-600 text-sm font-black">
            OD
          </span>
          <span>
            <span className="block text-sm font-bold tracking-wide">ODR</span>
            <span className="block text-[10px] tracking-wider text-steel-400 uppercase">
              Operator-Driven Reliability
            </span>
          </span>
        </Link>

        <nav className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-steel-700 text-white'
                    : 'text-steel-300 hover:bg-steel-800 hover:text-white',
                )
              }
            >
              <item.icon aria-hidden className="size-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-steel-800 p-3">
          <div className="mb-2 flex items-center gap-2 px-1">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-steel-700 text-xs font-bold text-white">
              {user ? initials(user.fullName) : '?'}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-white">
                {user?.fullName}
              </span>
              <span className="block truncate text-xs text-steel-400">
                {user?.roles.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(', ')}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-steel-300 hover:bg-steel-800 hover:text-white"
          >
            <LogOut aria-hidden className="size-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-steel-200 bg-white safe-top">
          <div className="flex h-14 items-center gap-2 px-3 lg:px-6">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="rounded-lg p-2 text-steel-600 hover:bg-steel-100 lg:hidden"
            >
              <Menu aria-hidden className="size-5" />
            </button>

            <Link to="/" className="flex items-center gap-2 lg:hidden">
              <span className="grid size-7 place-items-center rounded-md bg-steel-900 text-xs font-black text-white">
                OD
              </span>
              <span className="text-sm font-bold text-steel-900">ODR</span>
            </Link>

            <div className="flex-1" />

            <SyncIndicator />

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="rounded-lg p-2 text-steel-600 hover:bg-steel-100"
            >
              <Search aria-hidden className="size-5" />
            </button>

            <NotificationBell />
          </div>
        </header>

        <main className="min-w-0 flex-1 pb-20 lg:pb-0">{children}</main>

        {/* Phone bottom navigation */}
        <nav
          aria-label="Main"
          className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-steel-200 bg-white safe-bottom lg:hidden"
        >
          {primary.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                clsx(
                  'flex min-h-touch flex-col items-center justify-center gap-1 px-1 pt-2 text-[11px] font-semibold',
                  isActive ? 'text-info-700' : 'text-steel-500',
                )
              }
            >
              <item.icon aria-hidden className="size-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Phone drawer */}
      {menuOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-steel-950/50"
            onClick={() => setMenuOpen(false)}
          />
          <div className="relative flex h-full w-72 flex-col bg-steel-900 safe-top">
            <div className="flex items-center justify-between px-4 py-4">
              <span className="text-sm font-bold text-white">ODR</span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
                className="rounded-lg p-2 text-steel-300 hover:bg-steel-800"
              >
                <X aria-hidden className="size-5" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 overflow-y-auto px-2">
              {visible.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    clsx(
                      'mb-0.5 flex min-h-touch items-center gap-3 rounded-lg px-3 text-sm font-medium',
                      isActive
                        ? 'bg-steel-700 text-white'
                        : 'text-steel-300 hover:bg-steel-800 hover:text-white',
                    )
                  }
                >
                  <item.icon aria-hidden className="size-5 shrink-0" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="border-t border-steel-800 p-3 safe-bottom">
              <p className="px-1 pb-2 text-sm font-semibold text-white">{user?.fullName}</p>
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-steel-300 hover:bg-steel-800"
              >
                <LogOut aria-hidden className="size-4" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
  back,
}: {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  back?: { to: string; label: string };
}) {
  return (
    <div className="border-b border-steel-200 bg-white px-4 py-4 lg:px-6">
      {back ? (
        <Link
          to={back.to}
          className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-info-700 hover:underline"
        >
          ← {back.label}
        </Link>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-steel-900 lg:text-2xl">{title}</h1>
          {subtitle ? <div className="mt-1 text-sm text-steel-500">{subtitle}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

export { Bell };
